import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { medusa } from "#/lib/medusa";
import type {
  PrivateCategory,
  RedeemResponse,
} from "#/lib/medusa-types/private-products";

const privateCategorySchema = z.object({
  slug: z.string().min(1),
  label: z.string().min(1),
  teaser: z.string().nullable(),
});

const privateCategoryListSchema = z.object({
  categories: z.array(privateCategorySchema),
});

const redeemResponseSchema = z.object({
  slug: z.string().min(1),
  label: z.string().min(1),
  teaser: z.string().nullable(),
  unlockedSlugs: z.array(z.string().min(1)),
  token: z.string().min(1),
  expiresInSeconds: z.number().int().positive(),
});

const mySlugsResponseSchema = z.object({
  unlockedSlugs: z.array(z.string().min(1)),
  token: z.string().min(1),
  expiresInSeconds: z.number().int().positive(),
});

type FetchErrorLike = { status?: number };

function isInvalidCode(err: unknown): boolean {
  const status = (err as FetchErrorLike | undefined)?.status;
  return status === 404;
}

function isUnauthenticated(err: unknown): boolean {
  const status = (err as FetchErrorLike | undefined)?.status;
  return status === 401 || status === 403;
}

export const listPrivateCategories = createServerFn().handler(
  async (): Promise<PrivateCategory[]> => {
    const res = await medusa.client.fetch<unknown>("/store/private-products");
    return privateCategoryListSchema.parse(res).categories;
  },
);

export type RedeemResult =
  | { ok: true; data: RedeemResponse }
  | { ok: false; error: "invalid_code" | "unknown" };

export const redeemPrivateCode = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      code: z.string().min(1).max(64),
    }),
  )
  .handler(async ({ data }): Promise<RedeemResult> => {
    try {
      const res = await medusa.client.fetch<unknown>(
        "/store/private-products/redeem",
        {
          method: "POST",
          body: { code: data.code },
        },
      );
      return { ok: true, data: redeemResponseSchema.parse(res) };
    } catch (err) {
      if (isInvalidCode(err)) {
        return { ok: false, error: "invalid_code" };
      }
      return { ok: false, error: "unknown" };
    }
  });

export type MySlugsResult =
  | {
      ok: true;
      data: {
        unlockedSlugs: string[];
        token: string;
        expiresInSeconds: number;
      };
    }
  | { ok: false; reason: "unauthenticated" | "unknown" };

export const getMyUnlockedSlugs = createServerFn().handler(
  async (): Promise<MySlugsResult> => {
    try {
      const res = await medusa.client.fetch<unknown>(
        "/store/private-products/me",
      );
      return { ok: true, data: mySlugsResponseSchema.parse(res) };
    } catch (err) {
      if (isUnauthenticated(err)) {
        return { ok: false, reason: "unauthenticated" };
      }
      return { ok: false, reason: "unknown" };
    }
  },
);
